import { DashboardDataQuery } from '../DashboardData.generated';

export interface IssuesCount {
	critical: number;
	serious: number;
	total: number;
	allIssues: number; // Total of all severities (not just critical + serious)
	day: Date;
}

export interface DashboardDataResult {
	regularPeriod: IssuesCount[];
	doublePeriod: IssuesCount[];
}

export const transformDashboardDataResults = (
	rawResults: DashboardDataQuery
): DashboardDataResult => {
	// Regular period data - combine Web SDK and WFA data
	const regularPeriod = combineProductData(rawResults?.wsdkIssuesCount, rawResults?.wfaIssuesCount);

	// Double period data - combine Web SDK and WFA data
	const doublePeriod = combineProductData(
		rawResults?.wsdkIssuesCount2n,
		rawResults?.wfaIssuesCount2n
	);

	return {
		regularPeriod,
		doublePeriod
	};
};

const combineProductData = (
	wsdkData: DashboardDataQuery['wsdkIssuesCount'],
	wfaData: DashboardDataQuery['wfaIssuesCount']
): IssuesCount[] => {
	// Create a map to store all unique dates with their combined counts
	const dateMap = new Map<
		string,
		{ critical: number; serious: number; total: number; allIssues: number }
	>();

	// Process Web SDK data
	(wsdkData || [])
		.filter(
			(issue): issue is NonNullable<typeof issue> => issue !== null && !!issue.run_time_truncDay
		)
		.forEach((issue) => {
			const date = issue.run_time_truncDay as string;
			const existing = dateMap.get(date) || { critical: 0, serious: 0, total: 0, allIssues: 0 };
			dateMap.set(date, {
				...existing,
				critical: existing.critical + (issue.critical_issues_sum || 0),
				serious: existing.serious + (issue.serious_issues_sum || 0),
				allIssues: existing.allIssues + (issue.total_issues_sum || 0)
			});
		});

	// Process WFA data
	(wfaData || [])
		.filter(
			(issue): issue is NonNullable<typeof issue> => issue !== null && !!issue.run_time_truncDay
		)
		.forEach((issue) => {
			const date = issue.run_time_truncDay as string;
			const existing = dateMap.get(date) || { critical: 0, serious: 0, total: 0, allIssues: 0 };
			dateMap.set(date, {
				...existing,
				critical: existing.critical + (issue.critical_issues_sum || 0),
				serious: existing.serious + (issue.serious_issues_sum || 0),
				allIssues: existing.allIssues + (issue.total_issues_sum || 0)
			});
		});

	// Convert map to array, calculate total, and sort by date
	return Array.from(dateMap.entries())
		.map(([dateStr, counts]) => ({
			critical: counts.critical,
			serious: counts.serious,
			total: counts.critical + counts.serious, // Calculate total from critical + serious (for chart compatibility)
			allIssues: counts.allIssues, // Total of all severities
			day: new Date(dateStr)
		}))
		.sort((a, b) => a.day.getTime() - b.day.getTime());
};
